﻿import React, { useState, useEffect } from 'react';
import { CrudFunctions } from '../utilities/crud-functions';
import { Decimal4Format } from '../templates/pw-formatting';
import { CalculatedComponentActual } from '../models/calculated-component-actual';
import { BatchProductionCalculation } from '../models/Calculations/batch-production-calculation';
import { RawMaterialBatch } from '../models/raw-material-batch';
import { ComponentActual } from '../models/component-actual';
import { DataTemplates } from '../templates/data-templates';
import { PWMath } from '../utilities/pw-math';
import { PWDates } from '../utilities/pw-dates';
import { Asset } from '../models/asset';
import { UserDataStore } from '../store/userDataStore';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { ItemBasicInformation } from '../models/item-basic-information';

import DataGrid, {
    Column,
    Selection,
    Paging,
    Toolbar,
    Summary,
    Lookup,
    Editing,
    TotalItem,
    Button as ColumnButton,
    Item as ToolBarItem,
    LoadPanel
} from 'devextreme-react/data-grid';

import { Button } from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';

const decimalFormat = {
    type: "fixedPoint",
    precision: 4
}

//TODO:
const _WEIGHT_MULTIPLIER = 0.001;
const _EDIT_7 = "#ec0533";
const _EDIT_30 = "#f38118";

let key = 1;

export interface Props {
    batchId: string;
    balances?: Asset[];
    isReadOnly: boolean;
    onComponentsWeightChanged?: (componentsWeight?: number) => void;
}

export default function ComponentsActualDataComponent(props: Props) {
    const [batchProductionCalculation, setBatchProductionCalculation] =
        useState<BatchProductionCalculation>();

    const [selectedComponentActual, setSelectedComponentActual] =
        useState<CalculatedComponentActual>();

    const [availableLots, setAvailableLots] =
        useState<RawMaterialBatch[]>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, [props.batchId]);

    const user = UserDataStore.getCurrentUser();
    const canEditRoles = ["SA", "QCBS-A", "QCBS-L", "QCBS-T"];
    const canEdit = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));

    key++;

    return (
        <div>
            <div>
                <DataGrid id="ProductionBenchSheetComponentsActualDataGrid"
                    height="20rem"
                    wordWrapEnabled={true}
                    keyExpr="componentActualId"
                    dataSource={batchProductionCalculation && batchProductionCalculation.targetWeights ?
                        batchProductionCalculation.targetWeights : []}
                    allowColumnResizing={true}
                    onSelectionChanged={onComponentsActualSelectionChanged}
                    onRowValidating={onComponentsActualRowValidating}
                    onEditingStart={onComponentsActualEditingStart}
                    onCellPrepared={componentsActualButtonColumnRender}
                    columnResizingMode='nextColumn'
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    showBorders={true}>

                    <LoadPanel enabled={true} />
                    <Paging enabled={false} />
                    <Selection mode='single' />

                    <Column type="buttons" width="8rem">
                        <ColumnButton disabled={!canEdit}
                            name="edit" />
                        <ColumnButton disabled={!canEdit}
                            name="delete" />
                        <ColumnButton hint="audit information" icon="clock"
                            onClick={onComponentsActualAuditButtonClicked} />
                        <ColumnButton hint="information" icon="info"
                            onClick={showComponentsActualInformationDialog} />
                    </Column>

                    <Column dataField='elution' dataType="number" allowEditing={false}
                        width='4rem' alignment='center' cssClass="table-vertical-align" />

                    <Column dataField='partNumber' caption="Part#" dataType="string"
                        allowEditing={false} width='10rem' cssClass="table-vertical-align" />

                    <Column dataField='vendorPartNumber' dataType="string" caption="Vendor Part#"
                        allowEditing={false} width='10rem' cssClass="table-vertical-align" />

                    <Column dataField='productBatchId' caption="Lot#" width='10rem'
                        cssClass="table-vertical-align" cellRender={componentActualLotFieldRender} >
                        <Lookup dataSource={availableLots ? availableLots : []}
                            displayExpr='batchName'
                            valueExpr='rawMaterialBatchId' />
                    </Column>

                    {/*
                    <Column dataField='productBatchVendorPartNumber' caption="Vendor Lot#"
                        dataType="string" width='10rem'
                        cssClass="table-vertical-align" />
                        */}

                    <Column dataField='purity' dataType="number" allowEditing={false}
                        format={decimalFormat} width='6rem' cssClass="table-vertical-align" />

                    <Column dataField='assetId' width='8rem' caption="Asset"
                        cssClass="table-vertical-align">
                        <Lookup dataSource={props.balances ? props.balances : []}
                            displayExpr='assetName'
                            valueExpr='assetId' />
                    </Column>

                    <Column dataField='weight' caption="Weight(mg)"
                        cellRender={componentActualWeightFieldRender}
                        dataType="number" format={decimalFormat} width='6rem' cssClass="table-vertical-align" />

                    <Column dataField='targetWeight' caption="Target Weight(mg)" allowEditing={false}
                        dataType="number" format={decimalFormat} width='8rem' cssClass="table-vertical-align" />

                    <Column dataField='lowTarget' allowEditing={false}
                        dataType="number" format={decimalFormat} width='6.5rem' cssClass="table-vertical-align" />

                    <Column dataField='highTarget' allowEditing={false}
                        dataType="number" format={decimalFormat} width='6.5rem' cssClass="table-vertical-align" />

                    <Column />

                    <Summary>
                        <TotalItem column="weight" summaryType="sum" alignment="left" valueFormat={Decimal4Format} />
                    </Summary>

                    <Toolbar>
                        <ToolBarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Components Actual</div>
                            </div>
                        </ToolBarItem>

                        {/*
                        {canEdit &&
                            <ToolBarItem location="after">
                                <Button text="Restore" hint="Restore Components Actual" icon="refresh"
                                    width="6rem" onClick={onRestoreComponentsActualClick} />
                            </ToolBarItem>
                        }
                        */}
                    </Toolbar>

                    <Editing mode="row"
                        useIcons={true}
                        allowAdding={true}
                        allowUpdating={true}
                        allowDeleting={true} />

                </DataGrid>
            </div>

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width='25rem'
                    height='21rem'
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }

        </div>
    )

    function onComponentsActualEditingStart(e: any) {
        console.log("onComponentsActualEditingStart");
        console.log(e);

        if (e.data && e.data.productBatches) {
            setAvailableLots(e.data.productBatches);
        }

        setSelectedComponentActual(e.data);
    }

    function onComponentsActualSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0)
            setSelectedComponentActual(e.selectedRowsData[0]);
    }

    async function onRestoreComponentsActualClick() {
        if (props.batchId) {
            return await restoreComponentsActual(props.batchId)
                .then(() => {
                    getComponentsActual();

                    //showToast("Components Actual Restored", "success");

                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Restoring Components Actual");
                })
        }
    }

    async function onComponentsActualRowValidating(e: any) {
        if (e.oldData) {
            let componentActual = e.oldData as ComponentActual;

            if (componentActual) {
                let updatedComponentActual = { ...componentActual, ...e.newData };

                console.log("about to update Comp Actual...");

                updateComponentActual(updatedComponentActual)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating Component Actual");
                    });
            }
        }
        else {
            if (e.newData) {
                let componentActual = e.newData as ComponentActual;

                if (componentActual) {
                    await insertComponentActual(componentActual)
                        .then(() => {
                            getComponentsActual();
                        })
                        .catch((res) => {
                            showMessage(res, ModalMessageType.Error, "Error Inserting Component Actual");
                        });
                }
            }
        }
    }

    async function getComponentsActual() {
        await CrudFunctions.getData(`calculations/batchproductioncalculation/${props.batchId}`)
            .then(data => {

                console.log("COMP ACTUELLE:");
                console.log(data);


                setBatchProductionCalculation(data);
                //setComponentsActual(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Components Actual");
            });
    }

    // POST functions

    async function insertComponentActual(componentsActual: ComponentActual) {
        return await CrudFunctions.postData("products/componentactual", componentsActual);
    }

    async function restoreComponentsActual(batchId: string) {
        return await CrudFunctions.putData(`products/restorecomponentsactual/${batchId}`, null);
    }

    //PUT Functions
    async function updateComponentActual(componentsActual: ComponentActual) {
        return await CrudFunctions.putData("products/componentactual", componentsActual);
    }

    function calculateComponentsTotalWeight() {
        if (batchProductionCalculation && batchProductionCalculation.targetWeights) {

            let totalWeight = batchProductionCalculation.targetWeights.reduce((accumulator, currentValue) => {
                return accumulator + (currentValue.weight ? currentValue.weight : 0);
            }, 0);

            console.log("CA: components Total Weight");
            console.log(totalWeight);

            if (props.onComponentsWeightChanged)
                props.onComponentsWeightChanged(totalWeight);
        }
    }


    async function getAllData() {
        await getComponentsActual();
    }

    //we need this cell render because the Lookup does not work as the Lookup items get filled
    //   after the component is rendered 
    function componentActualLotFieldRender(e: any) {
        if (e.data && e.data.productBatchId && e.data.productBatches) {
            let matchingBatch = e.data.productBatches.find(b => b.rawMaterialBatchId === e.data.productBatchId);

            if (matchingBatch)
                return (<div>{matchingBatch.batchName}</div>);
        }
        else {
            return (<div></div>);
        }
    }

    function componentActualWeightFieldRender(e: any) {
        if (e.data && e.data.componentActualId && e.data.elution && e.data.weight
            && batchProductionCalculation.targetWeights) {

            let weight = 0;
            let targetWeight = 0;

            const matchingElutionComponents =
                batchProductionCalculation.targetWeights.filter(ca => ca.elution === e.data.elution);

            if (matchingElutionComponents.length > 1) {
                let totalWeight = matchingElutionComponents.reduce((tot, c) =>
                    tot + (c.weight ? c.weight : 0), 0
                );

                weight = totalWeight;
            }
            else
                weight = e.data.weight;

            if (!e.data.targetWeight) {
                const matchingTargetWeights = batchProductionCalculation.targetWeights.filter(
                    ca => ca.elution === e.data.elution && ca.targetWeight);

                if (matchingTargetWeights && matchingTargetWeights.length > 0) {
                    targetWeight = matchingTargetWeights[0].targetWeight ? matchingTargetWeights[0].targetWeight : 0;
                }
            }
            else
                targetWeight = e.data.targetWeight;

            const highValue = PWMath.roundNumber(targetWeight + targetWeight * _WEIGHT_MULTIPLIER, 4);
            const lowValue = PWMath.roundNumber(targetWeight - targetWeight * _WEIGHT_MULTIPLIER, 4);

            if (weight > highValue)
                return <div className="high-color" >{e.data.weight}</div>
            else if (weight < lowValue)
                return <div className="low-color" >{e.data.weight}</div>
            else
                return <div className="valid-data" >{e.data.weight}</div>
        }
    }

    function componentsActualButtonColumnRender(e: any) {
        if (e.column && e.rowType === "data" && e.data && e.cellElement) {
            if (e.column.type === "buttons" && e.data.latestWeightUpdate) {
                //get the Clock icon element inside the datagrid cell:
                const clockIcon = e.cellElement.querySelector('.dx-icon-clock');

                if (clockIcon) {
                    let weightUpdateDate = new Date(e.data.latestWeightUpdate);

                    if (PWDates.isPreviousDateMoreRecentThanDays(weightUpdateDate, 7)) {
                        clockIcon.style.color = _EDIT_7;
                    }
                    else if (PWDates.isPreviousDateMoreRecentThanDays(weightUpdateDate, 30)) {
                        clockIcon.style.color = _EDIT_30;
                    }
                }
            }
        }
    }

    function onComponentsActualAuditButtonClicked(e: any) {

    }

    function showComponentsActualInformationDialog(e: any) {
        if (e.row && e.row.data) {
            const component = e.row.data as CalculatedComponentActual;

            if (component) {
                setSelectedComponentActual(component);

                let itemInformation: ItemBasicInformation = {
                    rowPointer: component.componentActualId,
                    createTimestamp: component.createTimestamp,
                    lastModifiedTimestamp: component.modifyTimestamp,
                    createUser: component.createUser,
                    modifyUser: component.modifyUser
                };

                setItemBasicInformation(itemInformation);
                setItemBasicInformationDialogVisible(true);
            }
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }
}